import React, { useEffect, useMemo, useRef } from "react"
import { useDispatch, useSelector } from "react-redux"
import { useTranslation } from "react-i18next"
import { RangeInput } from "../../components/input/RangeInput"
import { mortgageValidationSchema } from "../../utils/formValidationSchema"
import { Field, Form, Formik } from "formik"
import Button from "../../components/Button"
import {
  createMortgageInfo,
  getMortgageInfo,
} from "../../redux/valuations.slice"
import { EuroIcon } from "lucide-react"
import { useWidgetInfo } from "../../utils/useWidgetInfo"
import WidgetInfo from "../../components/WidgetInfo"
import ProviderModal from "./ProviderModal"
import { IconQuestion } from "../../assets/icons"

const MortgageForm = ({ }) => {
  const { t } = useTranslation()
  const formikRef = useRef(null)
  const dispatch = useDispatch()
  const {
    valuations,
    currentValuation,
    currentMortgage,
    mortgageLoading,
    successMessage,
  } = useSelector((store) => store.valuationsDetails)

  useEffect(() => {
    if (currentMortgage.id) formikRef.current.setValues({ ...currentMortgage })
  }, [currentMortgage])

  useEffect(() => {
    if (!currentMortgage?.id || successMessage === "toasts.mortgage-success")
      dispatch(
        getMortgageInfo({ authToken: valuations[currentValuation]?.authToken })
      )
  }, [successMessage])

  const onSubmit = async (values) => {
    dispatch(
      createMortgageInfo({
        ...values,
        latestEstimatedOfferPrice:
          valuations[currentValuation].valuation?.global?.property_value ?? 0,
        authToken: valuations[currentValuation]?.authToken,
      })
    )
  }

  const initialValues = useMemo(
    () =>
      currentMortgage.id
        ? { ...currentMortgage }
        : {
          type: "",
          rate: 0,
          provider: "",
          currentValue: null,
        },
    [currentMortgage, valuations, currentValuation]
  )

  const widgetInfoState = useWidgetInfo()

  return (
    <div className="bg-accumin-neutral-100 card p-4 w-full flex flex-col gap-2">
      <div className="flex justify-between">
        <span>{t("finances.current-mortgage-info")}</span>
        <div className="pl-4 pb-2 cursor-pointer">
          <IconQuestion
            className="fill-accumin-primary-electric-blue h-6 w-6 p-1 bg-accumin-secondary-sky-blue rounded-full"
            onClick={() =>
              widgetInfoState.handleOpen("labels.value-terminology", "", [
                {
                  title: "finances.current-mortgage",
                  key: "equity.fix-variable",
                },
                {
                  title: "finances.interest-rate",
                  key: "equity.interest-rate",
                },
              ])
            }
          />
        </div>
      </div>
      <Formik
        initialValues={initialValues}
        enableReinitialize={true}
        validationSchema={mortgageValidationSchema(t)}
        onSubmit={onSubmit}
        innerRef={formikRef}
      >
        {({
          isSubmitting,
          values,
          setValues,
          errors,
          touched,
          handleChange,
          submitForm,
        }) => (
          <Form className="flex flex-col gap-6">
            <div
              className="flex flex-row"
              role="group"
              aria-labelledby="type-radio-group"
            >
              <div className="rounded-l-lg border border-r-0 flex-1 flex items-center align-middle gap-2 h-12 p-4 bg-white">
                <Field
                  className="h-5 w-5 accent-accumin-primary-electric-blue"
                  name="type"
                  value="variable"
                  id="type_variable"
                  type="radio"
                />
                <label
                  className={`cursor-pointer ${(values.type === "variable" ? "text-accumin-neutral-500" : "text-accumin-neutral-400")
                    }`}
                  htmlFor="type_variable"
                >
                  {t("finances.variable")}
                </label>
              </div>
              <div className="rounded-r-lg border flex-1 flex items-center align-middle gap-2 h-12 p-4 bg-white">
                <Field
                  className="h-5 w-5 accent-accumin-primary-electric-blue"
                  name="type"
                  value="fixed"
                  id="type_fixed"
                  type="radio"
                />
                <label
                  className={`cursor-pointer ${(values.type === "fixed" ? "text-accumin-neutral-500" : "text-accumin-neutral-400")
                  }`}
                  htmlFor="type_fixed"
                >
                  {t("finances.fixed")}
                </label>
              </div>
            </div>
            {touched.type && errors.type && (
              <div className="text-destructive-300">{errors.type}</div>
            )}

            <div className="flex flex-col gap-4">
              <div className="flex justify-between items-center">
                <label className="text-accumin-neutral-400 font-semibold">
                  {t("finances.interest-rate")}
                </label>
                <span className="text-accumin-primary-electric-blue text-sm font-medium pr-1">
                  {values.rate ? values.rate.toFixed(1).replace(".", ",") + "%" : ""}
                </span>
              </div>
              <RangeInput
                value={values.rate ?? 0}
                onChange={(v) => setValues({ ...values, rate: v })}
                max={50}
              />
            </div>
            {touched.rate && errors.rate && (
              <div className="text-destructive-300">{errors.rate}</div>
            )}

            <div className="flex flex-col gap-4">
              <ProviderModal
                name="provider"
                placeholder={t("labels.select-provider")}
                className="h-14 w-full rounded-md border-transparent outline-1 outline outline-neutral-200 px-4 text-sm shadow-sm bg-white"
              />
              {touched.provider && errors.provider && (
                <div className="text-destructive-300">{errors.provider}</div>
              )}

              <div className="relative">
                <EuroIcon className="h-4 w-4 absolute top-1/2 right-5 transform -translate-y-1/2 text-neutral-300" />
                <input
                  placeholder={t("finances.current-mortgage")}
                  type={"number"}
                  className="border rounded-md p-2 h-14 box-border pl-4 w-full shadow-sm placeholder-neutral-300"
                  onChange={handleChange}
                  name="currentValue"
                  defaultValue={values.currentValue}
                />
              </div>
              {touched.currentValue && errors.currentValue && (
                <div className="text-destructive-300">
                  {errors.currentValue}
                </div>
              )}
            </div>

            <Button
              type="button"
              onClick={submitForm}
              disabled={isSubmitting}
              className={`${currentMortgage?.id
                ? "button-outline bg-accumin-functional-pastel-electric-blue text-accumin-primary-electric-blue"
                : "button-regular !bg-accumin-primary-electric-blue"
                } w-full`}
              isLoading={mortgageLoading}
            >
              {currentMortgage?.id
                ? t("finances.recalculate")
                : t("finances.calculate")}
            </Button>
          </Form>
        )}
      </Formik>
      <WidgetInfo state={widgetInfoState} />
    </div>
  )
}

export default MortgageForm
